/**
 * Keyboard shortcuts help modal.
 *
 * Triggered by the global `?` key (wired in App.tsx via the global
 * command-palette coordinator). Renders a grouped list of every shortcut
 * with kbd-style chips. Sentence case throughout, Momentum brand tokens
 * (bg-card, border-border, text-muted-foreground).
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Shortcut {
  keys: string[]; // rendered as separate kbd chips; joined with `then`
  label: string;
}

interface Group {
  title: string;
  items: Shortcut[];
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

const GROUPS: Group[] = [
  {
    title: "Global",
    items: [
      { keys: [MOD, "K"], label: "Open command palette" },
      { keys: ["?"], label: "Show keyboard shortcuts" },
      { keys: ["Esc"], label: "Close dialog or palette" },
    ],
  },
  {
    title: "Navigation",
    items: [
      { keys: [MOD, "1"], label: "Go to dashboard" },
      { keys: [MOD, "2"], label: "Go to calendar" },
      { keys: [MOD, "3"], label: "Go to clients" },
      { keys: [MOD, "4"], label: "Go to messages" },
      { keys: [MOD, "5"], label: "Go to earnings" },
      { keys: [MOD, "6"], label: "Go to insights" },
      { keys: [MOD, "7"], label: "Go to settings" },
    ],
  },
  {
    title: "Actions",
    items: [
      { keys: [MOD, "N"], label: "Add new client" },
      { keys: [MOD, "M"], label: "Open messages" },
    ],
  },
  {
    title: "Forms",
    items: [
      { keys: [MOD, "Enter"], label: "Save / submit form" },
      { keys: ["Esc"], label: "Cancel and close" },
    ],
  },
];

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md border border-border",
        "bg-muted px-1.5 font-mono text-[11px] font-medium text-foreground/80",
        "shadow-[0_1px_0_0_hsl(var(--border))]"
      )}
    >
      {children}
    </kbd>
  );
}

export function KeyboardShortcutsHelp({ open, onOpenChange }: KeyboardShortcutsHelpProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-card">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Keyboard shortcuts</DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 sm:grid-cols-2">
          {GROUPS.map((group) => (
            <section key={group.title} className="space-y-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.title}
              </h3>
              <ul className="space-y-1.5">
                {group.items.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-center justify-between gap-4 text-sm"
                  >
                    <span className="text-foreground/90">{item.label}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {item.keys.map((k, idx) => (
                        <Kbd key={idx}>{k}</Kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <p className="pt-2 text-xs text-muted-foreground">
          Tip: press{" "}
          <Kbd>{MOD}</Kbd>
          <span className="mx-1" />
          <Kbd>K</Kbd> anywhere to jump to a client, page, or action.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsHelp;
